import {
  setMicDevices,
  setVideoDevices,
  setAudioDevices,
  setPermissions,
  selectMicDevice,
  selectVideoDevice,
  selectAudioDevice,
  setMicLevel,
  setLoading,
  DeviceOption,
} from "store/modules/mediaDevices/actions";
import { changeUserPreferences } from "store/modules/user/actions";
import { createAppAsyncThunk } from "store/rootReducer";

import { customerPreferencesService } from "services/customerPreferences";

import { LogOperation } from "utils/entities/logOperation";

const formatDevices = (
  devices: MediaDeviceInfo[],
  fallback: string,
  useIndexedFallback = false,
): DeviceOption[] => {
  return devices.map((d, index) => {
    const fallbackLabel = useIndexedFallback
      ? `${fallback} ${index + 1}`
      : fallback;

    return {
      id: d.deviceId,
      name: d.label || fallbackLabel,
      label: d.label || fallbackLabel,
      selected: false,
    };
  });
};

const resolveSelection = (
  devices: DeviceOption[],
  current?: DeviceOption | null,
): DeviceOption | null => {
  if (!devices.length) return null;
  if (current) {
    const match = devices.find((device) => device.id === current.id);
    if (match) return match;
  }
  return devices[0];
};

const shouldUpdateSelection = (
  current?: DeviceOption | null,
  next?: DeviceOption | null,
): boolean => {
  if (!current && !next) return false;
  if (!current || !next) return true;
  return (
    current.id !== next.id ||
    current.label !== next.label ||
    current.name !== next.name
  );
};

const safeEnumerateDevices = async (): Promise<MediaDeviceInfo[]> => {
  try {
    return await navigator.mediaDevices.enumerateDevices();
  } catch (error) {
    console.error("Erro ao listar dispositivos", error);
    throw error;
  }
};

const extractDeviceLists = (devices: MediaDeviceInfo[]) => {
  const micDevices = devices.filter((device) => device.kind === "audioinput");
  const videoDevices = devices.filter((device) => device.kind === "videoinput");
  const audioDevices = devices.filter((device) => device.kind === "audiooutput");

  return {
    mic: formatDevices(micDevices, "Microfone", true),
    video: formatDevices(videoDevices, "Câmera", true),
    audio: formatDevices(audioDevices, "Alto-falante", true),
  };
};

const syncSelections = (
  dispatch: any,
  devices: {
    mic: DeviceOption[];
    video: DeviceOption[];
    audio: DeviceOption[];
  },
  current: {
    selectedMic?: DeviceOption | null;
    selectedVideo?: DeviceOption | null;
    selectedAudio?: DeviceOption | null;
  },
) => {
  const nextMic = resolveSelection(devices.mic, current.selectedMic);
  const nextVideo = resolveSelection(devices.video, current.selectedVideo);
  const nextAudio = resolveSelection(devices.audio, current.selectedAudio);

  if (shouldUpdateSelection(current.selectedMic, nextMic)) {
    dispatch(selectMicDevice(nextMic ?? null));
  }

  if (shouldUpdateSelection(current.selectedVideo, nextVideo)) {
    dispatch(selectVideoDevice(nextVideo ?? null));
  }

  if (shouldUpdateSelection(current.selectedAudio, nextAudio)) {
    dispatch(selectAudioDevice(nextAudio ?? null));
  }
};

const parseDevicePreference = (
  preference?: string | null,
): DeviceOption | null => {
  if (!preference) return null;
  try {
    const parsed = JSON.parse(preference) as DeviceOption | null;
    if (parsed && typeof parsed.id === "string") {
      return parsed;
    }
  } catch (error) {
    console.warn("Erro ao interpretar preferência de dispositivo", error);
  }
  return null;
};

const applyPreferenceFallback = (
  devices: DeviceOption[],
  preference: DeviceOption | null,
  fallback: DeviceOption | null,
): DeviceOption | null => {
  if (preference?.id) {
    const match = devices.find((device) => device.id === preference.id);
    if (match) return match;
  }
  return fallback;
};

export const checkPermissionsThunk = createAppAsyncThunk(
  "mediaDevices/checkPermissions",
  async (_, { dispatch }) => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      dispatch(
        setPermissions({
          mic: devices.some((device) => device.kind === "audioinput"),
          camera: devices.some((device) => device.kind === "videoinput"),
          audio: devices.some((device) => device.kind === "audiooutput"),
          stream:
            devices.some((device) => device.kind === "videoinput") &&
            devices.some((device) => device.kind === "audioinput"),
        }),
      );
    } catch (error) {
      console.error("Erro ao verificar permissões", error);
      dispatch(
        setPermissions({
          mic: false,
          camera: false,
          audio: false,
          stream: false,
        }),
      );
    }
  },
);

export const getDevicesThunk = createAppAsyncThunk(
  "mediaDevices/getDevices",
  async (_, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));

      const devices = await safeEnumerateDevices();
      const formatted = extractDeviceLists(devices);

      dispatch(setMicDevices(formatted.mic));
      dispatch(setVideoDevices(formatted.video));
      dispatch(setAudioDevices(formatted.audio));

      const state = getState().mediaDevices;
      syncSelections(dispatch, formatted, {
        selectedMic: state.selectedMic,
        selectedVideo: state.selectedVideo,
        selectedAudio: state.selectedAudio,
      });
    } catch (err) {
      console.error("Erro ao obter devices", err);
    } finally {
      dispatch(setLoading(false));
    }
  },
);

export const persistPreferencesThunk = createAppAsyncThunk(
  "mediaDevices/persistPreferences",
  async (_, { getState, dispatch }) => {
    try {
      const state = getState();
      const mic = state.mediaDevices.selectedMic;
      const audio = state.mediaDevices.selectedAudio;
      const video = state.mediaDevices.selectedVideo;

      const userId = LogOperation.getUserId();
      const machineIp = LogOperation.getMachineIP();

      const micPreference = JSON.stringify(mic);
      const audioPreference = JSON.stringify(audio);
      const videoPreference = JSON.stringify(video);

      let preferences = state.user.preferences;

      if (!preferences?.id) {
        const { body } = await customerPreferencesService.GetByUserId(userId);
        preferences = body || null;
      }

      if (preferences?.id) {
        const { success } = await customerPreferencesService.put({
          id: preferences.id,
          audioPreference,
          videoPreference,
          micPreference,
          deviceRegistration: machineIp,
          userId,
          createIn: preferences.createIn,
          updateIn: new Date().toISOString(),
        });

        if (!success) throw new Error("Erro ao atualizar preferências");

        dispatch(changeUserPreferences(preferences));
      } else {
        const { body } = await customerPreferencesService.add({
          audioPreference,
          videoPreference,
          micPreference,
          deviceRegistration: machineIp,
          userId,
          createIn: new Date().toISOString(),
        });

        dispatch(changeUserPreferences(body));
      }
    } catch (err) {
      console.error("Erro ao persistir preferências", err);
    }
  },
);

let animationFrameId: number | undefined;
let audioContext: AudioContext | undefined;
let micStream: MediaStream | undefined;

export const stopMicLevelUpdate = async () => {
  if (animationFrameId !== undefined) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = undefined;
  }

  if (audioContext && audioContext.state !== "closed") {
    await audioContext.close();
    audioContext = undefined;
  }

  if (micStream) {
    micStream.getTracks().forEach((track) => track.stop());
    micStream = undefined;
  }
};

export const changeMicThunk = createAppAsyncThunk(
  "mediaDevices/changeMic",
  async (deviceId: string, { dispatch, getState }) => {
    try {
      const mic = getState().mediaDevices.micDevices.find(
        (m) => m.id === deviceId,
      );
      if (!mic) return;

      await stopMicLevelUpdate();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
      });

      micStream = stream;
      dispatch(selectMicDevice(mic));

      audioContext = new AudioContext();
      const micSource = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      micSource.connect(analyser);
      analyser.fftSize = 256;
      const buffer = new Uint8Array(analyser.frequencyBinCount);

      const updateMicLevel = () => {
        analyser.getByteFrequencyData(buffer);
        const avg = buffer.reduce((a, b) => a + b, 0) / buffer.length;
        dispatch(setMicLevel(avg));
        animationFrameId = requestAnimationFrame(updateMicLevel);
      };

      updateMicLevel();
    } catch (err) {
      console.error("Erro ao trocar mic", err);
    }
  },
);

export const changeVideoThunk = createAppAsyncThunk(
  "mediaDevices/changeVideo",
  async (deviceId: string, { dispatch, getState }) => {
    const video = getState().mediaDevices.videoDevices.find(
      (videoDevice: DeviceOption) => videoDevice.id === deviceId,
    );
    if (video) dispatch(selectVideoDevice(video));
  },
);

export const changeAudioThunk = createAppAsyncThunk(
  "mediaDevices/changeAudio",
  async (deviceId: string, { dispatch, getState }) => {
    const audio = getState().mediaDevices.audioDevices.find(
      (audioDevice: DeviceOption) => audioDevice.id === deviceId,
    );

    if (audio) {
      dispatch(selectAudioDevice(audio));

      if ("setSinkId" in HTMLMediaElement.prototype) {
        try {
          const testAudio = new Audio();
          if (typeof testAudio.setSinkId === "function") {
            await testAudio.setSinkId(deviceId);
          }
        } catch (err) {
          console.warn("Erro ao aplicar setSinkId", err);
        }
      }
    }
  },
);

export const restorePreferencesThunk = createAppAsyncThunk(
  "mediaDevices/restorePreferences",
  async (_, { dispatch, getState }) => {
    try {
      const userId = LogOperation.getUserId();
      if (!userId) return;

      const devices = await safeEnumerateDevices();
      const formatted = extractDeviceLists(devices);

      dispatch(setMicDevices(formatted.mic));
      dispatch(setVideoDevices(formatted.video));
      dispatch(setAudioDevices(formatted.audio));

      const { selectedMic, selectedVideo, selectedAudio } =
        getState().mediaDevices;

      let nextMic = resolveSelection(formatted.mic, selectedMic);
      let nextVideo = resolveSelection(formatted.video, selectedVideo);
      let nextAudio = resolveSelection(formatted.audio, selectedAudio);

      const { body } = await customerPreferencesService.GetByUserId(userId);
      if (body) {
        dispatch(changeUserPreferences(body));

        const micPref = parseDevicePreference(body.micPreference);
        const audioPref = parseDevicePreference(body.audioPreference);
        const videoPref = parseDevicePreference(body.videoPreference);

        nextMic = applyPreferenceFallback(formatted.mic, micPref, nextMic);
        nextAudio = applyPreferenceFallback(formatted.audio, audioPref, nextAudio);
        nextVideo = applyPreferenceFallback(formatted.video, videoPref, nextVideo);
      }

      if (shouldUpdateSelection(selectedMic, nextMic)) {
        dispatch(selectMicDevice(nextMic ?? null));
      }
      if (shouldUpdateSelection(selectedAudio, nextAudio)) {
        dispatch(selectAudioDevice(nextAudio ?? null));
      }
      if (shouldUpdateSelection(selectedVideo, nextVideo)) {
        dispatch(selectVideoDevice(nextVideo ?? null));
      }
    } catch (error) {
      console.error("Erro ao restaurar preferências:", error);
    }
  },
);
